/*
 * <product-media-gallery>: the product gallery's carousel, its steps and
 * status, the zoom dialog, and following the selected variant's media.
 *
 * An asset rather than the snippet's own {% javascript %}: the gallery is also
 * served inside the quick-add drawer, whose content arrives by fetch on pages
 * that never rendered the snippet, so they never received its bundle. The
 * snippet loads this file where it renders; the drawer imports it before it
 * shows the gallery.
 */

(function () {
  'use strict';

  class ProductMediaGallery extends HTMLElement {
    connectedCallback() {
      this.setAttribute('data-enhanced', '');

      this.viewport = this.querySelector('.product-gallery__viewport');
      this.items = Array.from(this.querySelectorAll('.product-gallery__item'));
      this.knob = this.querySelector('.product-gallery__knob');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.status = this.querySelector('[data-gallery-status]');
      this.buttons = Array.from(this.querySelectorAll('[data-gallery-step]'));
      this.index = -1;
      this.multiple = this.items.length > 1;

      this.settled = false;
      this.activeMedia = this.dataset.activeMedia || null;

      if (!this.viewport) return;

      // `column` is the stacked layout, where every picture is on the page at
      // once; a carousel never becomes one.
      this.column = window.matchMedia(this.dataset.layout === 'carousel' ? 'not all' : '(min-width: 990px)');
      this.column.addEventListener('change', this.onLayoutChange);

      document.addEventListener('variant:changed', this.onVariantChanged);

      this.dialog = this.querySelector('[data-gallery-zoom-dialog]');
      this.addEventListener('click', this.onZoomClick);
      this.addEventListener('focusin', this.onFocusIn);
      if (this.dialog) this.dialog.addEventListener('close', this.onZoomClose);

      if (!this.multiple) {
        this.applyLayout();
        return;
      }

      this.observer = new IntersectionObserver(this.onIntersect, {
        root: this.viewport,
        threshold: 0.6,
      });

      this.applyLayout();

      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);

      const start = Math.max(0, this.indexOfMedia(this.dataset.activeMedia));
      this.setIndex(start);

      if (start > 0 && !this.column.matches) this.goTo(start, 'auto');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.settled = true;
        });
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.column?.removeEventListener('change', this.onLayoutChange);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('click', this.onZoomClick);
      this.removeEventListener('focusin', this.onFocusIn);
      this.dialog?.removeEventListener('close', this.onZoomClose);
      document.removeEventListener('variant:changed', this.onVariantChanged);
    }

    onLayoutChange = () => this.applyLayout();

    applyLayout() {
      const carousel = this.multiple && !this.column.matches;

      if (carousel) {
        this.viewport.setAttribute('tabindex', '0');
        this.viewport.setAttribute('aria-roledescription', this.dataset.carouselLabel || '');
      } else {
        this.viewport.removeAttribute('tabindex');
        this.viewport.removeAttribute('aria-roledescription');
      }

      if (!this.observer) return;

      if (carousel) {
        const width = this.viewport.clientWidth;
        this.setIndex(width > 0 ? Math.round(this.viewport.scrollLeft / width) : 0);
        this.items.forEach((item) => this.observer.observe(item));
        return;
      }

      this.observer.disconnect();
    }

    get behavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    indexOfMedia(mediaId) {
      if (mediaId == null) return -1;
      return this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    }

    onIntersect = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.setIndex(this.items.indexOf(entry.target));
      }
    };

    onKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      if (this.column.matches) return;
      if (event.target !== this.viewport) return;

      event.preventDefault();
      const towardsEnd = getComputedStyle(this.viewport).direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
      this.goTo(this.index + (event.key === towardsEnd ? 1 : -1));
    };

    onClick = (event) => {
      const button = event.target.closest('[data-gallery-step]');
      if (!button) return;
      this.goTo(this.index + Number(button.dataset.galleryStep));
    };

    onVariantChanged = (event) => {
      const { sectionId, mediaId } = event.detail || {};
      if (sectionId && sectionId !== this.dataset.sectionId) return;

      const index = this.indexOfMedia(mediaId);
      if (index < 0) return;

      // A variant that shows the photograph already in hand (another size of
      // the same colour) leaves the shopper where they were looking.
      if (String(mediaId) === this.activeMedia) return;
      this.activeMedia = String(mediaId);

      this.activate(index);
    };

    // Below 750px the buy box slides over the gallery that holds at the top
    // of the screen, and a browser takes a held element for one already in
    // view: a control reached by keyboard under the sheet is never scrolled
    // to. Waiting a frame lets the browser do its own scrolling first.
    onFocusIn = (event) => {
      const target = event.target;
      requestAnimationFrame(() => {
        if (!target.isConnected || !this.contains(target)) return;

        const box = target.getBoundingClientRect();
        const x = Math.min(Math.max(box.left + box.width / 2, 0), window.innerWidth - 1);
        const y = Math.min(Math.max(box.top + box.height / 2, 0), window.innerHeight - 1);
        const top = document.elementFromPoint(x, y);
        if (!top || this.contains(top)) return;

        this.closest('.product-gallery')?.parentElement?.scrollIntoView({
          behavior: this.behavior,
          block: 'start',
        });
      });
    };

    activate(index) {
      if (this.column.matches) {
        this.items[index].scrollIntoView({ behavior: this.behavior, block: 'start' });
        return;
      }
      this.goTo(index);
    }

    setIndex(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.items.length) return;
      if (index === this.index) return;
      if (this.items[this.index]) this.pauseMedia(this.items[this.index]);
      this.index = index;

      if (this.knob) {
        this.knob.style.setProperty('--gallery-index', index);
      }

      const label = this.items[index].dataset.position;
      if (this.counter) this.counter.textContent = label;
      if (this.status && this.settled) this.status.textContent = label;

      for (const button of this.buttons) {
        const step = Number(button.dataset.galleryStep);
        button.disabled = !this.items[index + step];
      }
    }

    // A slide swiped away is out of sight but not out of earshot: a video
    // left playing there keeps talking over the slide the shopper moved to.
    // Embeds are cross-origin, so they are asked through the player interface
    // their address turns on (see `snippets/video-embed-url.liquid`).
    pauseMedia(item) {
      item.querySelectorAll('video').forEach((video) => video.pause());

      item.querySelectorAll('iframe').forEach((frame) => {
        const message = frame.src.includes('vimeo')
          ? { method: 'pause' }
          : { event: 'command', func: 'pauseVideo', args: '' };
        frame.contentWindow?.postMessage(JSON.stringify(message), '*');
      });
    }

    onZoomClick = (event) => {
      if (!this.dialog) return;

      const close = event.target.closest('[data-gallery-zoom-close]');
      if (close && this.contains(close)) {
        this.dialog.close();
        return;
      }

      const trigger = event.target.closest('[data-gallery-zoom]');
      if (trigger && this.contains(trigger)) this.openZoom(trigger);
    };

    onZoomClose = () => {
      const opener = this.zoomOpener;
      this.zoomOpener = null;
      if (opener && this.contains(opener) && opener.isConnected) opener.focus();
    };

    openZoom(trigger) {
      const item = this.items.find((i) => i.dataset.mediaId === trigger.dataset.mediaId);
      const source = item?.querySelector('img');
      const target = this.dialog.querySelector('[data-gallery-zoom-image]');
      if (!source || !target) return;

      target.src = source.currentSrc || source.src;
      target.srcset = source.srcset || '';
      target.sizes = '100vw';
      target.alt = source.alt || '';

      this.zoomOpener = trigger;
      this.dialog.showModal();
    }

    goTo(index, behavior) {
      const item = this.items[index];
      if (!item) return;

      this.viewport.scrollTo({
        left: item.offsetLeft - this.viewport.offsetLeft,
        behavior: behavior || this.behavior,
      });
    }
  }

  if (!customElements.get('product-media-gallery')) {
    customElements.define('product-media-gallery', ProductMediaGallery);
  }
})();
